import {objectFromEntries, entry, areSetsEqual} from '@benchling/js-utils';
import {useEffect, useMemo, useRef, useState} from 'react';
import _ from 'underscore';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {GetAnalyticsDashboard} from 'insights/analytics-dashboards/details/graphql/__generated/GetAnalyticsDashboard';
import {
  analyticsDashboardGlobalScreenState,
  getVisibleAnalyticsQueryIds,
} from 'insights/analytics-dashboards/details/view-state/ScreenState';
import {AnalyticsDashboardSpecRequest} from 'insights/analytics-dashboards/RESTAnalyticsDashboardTypes';
import {
  getAnalyticsQueryFromViewStateId,
  getAnalyticsQueryViewStateId,
} from 'insights/analytics-queries/AnalyticsQueryViewState';
import {AnalyticsQuerySpecRequest} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import {runAnalyticsQuery} from 'insights/analytics-queries/run-query/RunAnalyticsQueryActions';
import runWarehouseQueryGlobalState from 'insights/analytics-queries/run-query/runWarehouseQueryGlobalState';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {getRunQueryData, restGetRunQueryData} from 'insights/parameters/data/AnalyticsQueryOperations';
import usePrevious from 'ui-library/hooks/usePrevious';
import {MakeFieldsRequired} from 'util/Types';

import processAnalyticsQuery from './processAnalyticsQuery';
import RunQueryDatasById from './RunQueryDatasById';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

interface Actions {
  runAnalyticsQuery: typeof runAnalyticsQuery;
}

export interface UseRunDashboardQueriesInput {
  actions: Actions;
  analyticsDashboard: AnalyticsDashboard;
  // Since the hook is rendered before `AnalyticsDashboardDetailsContext` is provided, it needs to take the
  // dashboard view state id through a prop.
  analyticsDashboardViewStateId: string;
}

interface UseRunDashboardQueriesOutput {
  runOnChangeParameterValues(newParameterValues: Array<AnalyticsParameterValue>): void;
  runOnUpdateDashboard(newDashboard: AnalyticsDashboardSpecRequest): void;
}

/**
 * Custom hook that handles running the dashboard's queries
 *
 * It solves for these goals:
 *   - Run the query if it's not already fetched or its input changes (parameter configs, values, or SQL
 *     query)
 *       - Notably, if a parameter changes, then only re-run the query if it's affected (aka it uses the
 *         parameter)
 *   - Run the query as soon as the user changes the input
 *       - Run the query before the save completes (using REST objects like analyticsDashboardSpecRequest),
 *         instead of after the save completes (using the GraphQL objects like props.analyticsDashboard)
 *       - This makes the save and run happen in parallel, instead of serial, for a better user experience
 *   - Only run visible blocks
 *       - For example, if the user only has block editor open, then only run that block's query instead of
 *         all of the dashboard's queries. But be sure to re-run the other queries when they return to
 *         dashboard editor, if they were affected by the changes made.
 *
 * It takes the dashboard as input and returns callbacks as output. The caller should call these callbacks
 * when the user changes the input (like runOnUpdateDashboard when the user saves dashboard changes) so that
 * this hook can figure out which queries to run.
 *
 * Under the hood, the hook simply tracks the (old) data each query was last run with and the (new) data each
 * query should be run with. If the two are different for a visible block, then it runs that block's query.
 * The new data comes from the callback calls.
 */
export default function useRunDashboardQueries({
  actions,
  analyticsDashboard,
  analyticsDashboardViewStateId,
}: UseRunDashboardQueriesInput): UseRunDashboardQueriesOutput {
  const {analyticsQueries, parameterConfigs, parameterValues} = analyticsDashboard;
  const [screenState] = analyticsDashboardGlobalScreenState.useGlobalClientState(
    analyticsDashboardViewStateId
  );
  const visibleAnalyticsQueryViewStateIds = useMemo(
    () =>
      getVisibleAnalyticsQueryIds(analyticsDashboard, screenState).map((analyticsQueryId) =>
        getAnalyticsQueryViewStateId(analyticsQueryId, analyticsDashboardViewStateId)
      ),
    [analyticsDashboard, analyticsDashboardViewStateId, screenState]
  );
  const visibleAnalyticsQueryViewStateIdSet = useMemo(
    () => new Set(visibleAnalyticsQueryViewStateIds),
    [visibleAnalyticsQueryViewStateIds]
  );
  const prevVisibleAnalyticsQueryViewStateIdSet = usePrevious(visibleAnalyticsQueryViewStateIdSet);

  const [runQueryStatesById] = runWarehouseQueryGlobalState.useGlobalClientStates(
    visibleAnalyticsQueryViewStateIds
  );

  // State:
  //   - lastRunQueryDatasById: the data the query was last run with
  //       - Initialize lastRunQueryData from the initial dashboard
  //   - pendingRunQueryDatasById: the data the query should be run with
  //       - Initialize pendingRunQueryData to be empty
  //
  // We rely on getRunQueryData to filter the dashboard's parameters down to the query's parameters, so that
  // each runQueryData is minimal, with only the parameters the query uses. This lets us determine whether or
  // not a parameter change affects a query.
  const initialRunQueryDatasById = objectFromEntries(
    analyticsQueries.map((analyticsQuery) =>
      entry(
        analyticsQuery.id,
        getRunQueryData({parameterConfigs, parameterValues, sqlQuery: analyticsQuery.sqlQuery})
      )
    )
  );
  // Use ref because we don't want lastRunQueryData changes to cause re-renders
  const lastRunQueryDatasByIdRef = useRef<RunQueryDatasById>(initialRunQueryDatasById);
  // Use state because we want pendingRunQueryData changes to cause a re-render, so that it can be processed
  // with the useEffect hook below
  const [pendingRunQueryDatasById, setPendingRunQueryDatasById] = useState<RunQueryDatasById>({});
  const prevPendingRunQueryDatasById = usePrevious(pendingRunQueryDatasById);

  // Process visible blocks - run them if they should be run
  // This should be done if
  //   - pendingRunQueryData changed (new data to run with), or
  //   - visibleAnalyticsQueryIdSet changed (newly visible blocks that may need to be run)
  useEffect(() => {
    const skip =
      prevPendingRunQueryDatasById === pendingRunQueryDatasById &&
      prevVisibleAnalyticsQueryViewStateIdSet &&
      areSetsEqual(prevVisibleAnalyticsQueryViewStateIdSet, visibleAnalyticsQueryViewStateIdSet);
    if (skip) {
      return;
    }
    const analyticsQueriesById = _.indexBy(analyticsQueries, 'id');
    visibleAnalyticsQueryViewStateIds.forEach((analyticsQueryViewStateId) => {
      processAnalyticsQuery({
        actions,
        analyticsDashboardViewStateId,
        analyticsQuery: analyticsQueriesById[getAnalyticsQueryFromViewStateId(analyticsQueryViewStateId)],
        analyticsQueryViewStateId,
        lastRunQueryDatasByIdRef,
        parameterConfigs,
        parameterValues,
        pendingRunQueryDatasById,
        runQueryState: runQueryStatesById[analyticsQueryViewStateId],
        setPendingRunQueryDatasById,
      });
    });
  }, [
    actions,
    analyticsDashboard,
    analyticsDashboardViewStateId,
    analyticsQueries,
    parameterConfigs,
    parameterValues,
    pendingRunQueryDatasById,
    prevPendingRunQueryDatasById,
    visibleAnalyticsQueryViewStateIds,
    prevVisibleAnalyticsQueryViewStateIdSet,
    runQueryStatesById,
    visibleAnalyticsQueryViewStateIdSet,
  ]);

  // Return the callbacks that update pendingRunQueryData - they tell this hook that there's new data to run
  // with
  return {
    /**
     * Set pendingRunQueryData for all blocks on parameter value change
     *
     * For example, read-only user locally sets new parameter value.
     *
     * This is only exposed for the read-only case. Otherwise, parameter values could also be routed through
     * runOnUpdateDashboard.
     */
    runOnChangeParameterValues(newParameterValues: Array<AnalyticsParameterValue>): void {
      const newPendingRunQueryDatasById = objectFromEntries(
        analyticsQueries.map((analyticsQuery) =>
          entry(
            analyticsQuery.id,
            getRunQueryData({
              parameterConfigs,
              parameterValues: newParameterValues,
              sqlQuery: analyticsQuery.sqlQuery,
            })
          )
        )
      );
      setPendingRunQueryDatasById(newPendingRunQueryDatasById);
    },

    /**
     * Set pendingRunQueryData for all blocks on dashboard update
     *
     * For example, user saves new parameter config, value, or SQL query.
     */
    runOnUpdateDashboard(newDashboard: AnalyticsDashboardSpecRequest): void {
      const newAnalyticsQueries = newDashboard.analyticsQueries.filter(
        (analyticsQuery): analyticsQuery is MakeFieldsRequired<AnalyticsQuerySpecRequest, 'id'> =>
          analyticsQuery.id != null
      );
      const newPendingRunQueryDatasById = objectFromEntries(
        newAnalyticsQueries.map((newAnalyticsQuery) =>
          entry(
            newAnalyticsQuery.id,
            restGetRunQueryData({
              parameterConfigs: newDashboard.parameterConfigs,
              parameterValues: newDashboard.parameterValues,
              sqlQuery: newAnalyticsQuery.sqlQuery,
            })
          )
        )
      );
      setPendingRunQueryDatasById(newPendingRunQueryDatasById);
    },
  };
}
